import { valibotResolver } from "@hookform/resolvers/valibot";
import { LinkedinLogo } from "@phosphor-icons/react";
import clsx from "clsx";
import { useState } from "react";
import { useForm } from "react-hook-form";

import MaxLengthInput from "common/components/MaxLengthInput";
import { Button } from "common/components/ui/Button";
import {
  DialogBody,
  DialogFooter,
  DialogHeader,
} from "common/components/ui/Dialog";
import {
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
} from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import WorkspaceAccountSelector from "common/components/WorkspaceAccountSelector";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import { getSchemaMaxLength } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import useWorkspaceNavigate from "common/hooks/useWorkspaceNavigate";
import { CampaignNameSchema } from "common/schemas";

import useCampaignMutations from "../../datahooks/useCampaignMutations";

interface DuplicateCampaignProps {
  campaignId: string;
  name: string;
  accountIds: string[];
  isDrip: boolean;
  onCancel: () => void;
}

export default function DuplicateCampaign({
  campaignId,
  name,
  accountIds,
  isDrip,
  onCancel,
}: DuplicateCampaignProps) {
  const isDesktop = useTwBreakpoint("lg");
  const Footer = isDesktop ? DialogFooter : DrawerFooter;
  const [selectedAccountIds, setSelectedAccountIds] = useState(accountIds);

  const { duplicateCampaign, isDuplicatingCampaign } = useCampaignMutations();
  const { relativeNavigate } = useWorkspaceNavigate();

  const { accounts } = useSelectedWorkspaceContext();

  const workerAccounts = selectedAccountIds.length
    ? selectedAccountIds
    : accounts.map(({ id }) => id);

  const selection = isDrip
    ? (selectedAccountIds[0] ?? accounts[0].id)
    : selectedAccountIds;

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: `[Copy] ${name}`,
    },
    resolver: valibotResolver(CampaignNameSchema),
  });

  function onDuplicate(data: { name: string }) {
    duplicateCampaign({
      campaignId,
      name: data.name,
      accountIds: workerAccounts,
    }).then((campaign) => {
      relativeNavigate(`campaigns/${campaign.id}`);
    });
  }

  const Body = isDesktop ? DialogBody : DrawerBody;

  const Header = isDesktop ? DialogHeader : DrawerHeader;

  return (
    <>
      <Header title="Which account will run this campaign?" />

      <Body>
        <FormField
          label="Campaign name"
          htmlFor="campaignName"
          className="mb-8"
          error={errors.name?.message}
          size="lg"
        >
          <MaxLengthInput
            id="campaignName"
            variant="lg"
            characterLength={watch("name").length}
            maxLength={getSchemaMaxLength(CampaignNameSchema.entries.name)}
            placeholder="Campaign name"
            {...register("name")}
          />
        </FormField>

        <section className="flex flex-col justify-between gap-3 lg:flex-row lg:items-center">
          <div className="flex gap-x-2.5">
            <span
              className={clsx(
                "inline-flex size-10 shrink-0 items-center justify-center rounded-2xl",
                isDrip ? "bg-blue-100 text-blue-600" : "bg-black-100",
              )}
            >
              <LinkedinLogo size={20} />
            </span>

            <div className="flex flex-col gap-0.5">
              <h3 className="text-button-md">
                LinkedIn Account{isDrip ? "" : "s"}
              </h3>

              <p className="text-xs text-black-500">
                What LinkedIn account will run this campaign
              </p>
            </div>
          </div>

          <WorkspaceAccountSelector
            selection={selection}
            setSelection={(nextSelection) =>
              setSelectedAccountIds(
                Array.isArray(nextSelection) ? nextSelection : [nextSelection],
              )
            }
            className="w-48 max-lg:ml-[50px]"
          />
        </section>
      </Body>

      <Footer className="justify-end">
        <Button
          variant="secondary-black"
          onClick={onCancel}
          disabled={isDuplicatingCampaign}
        >
          Cancel
        </Button>

        <Button
          isLoading={isDuplicatingCampaign}
          onClick={handleSubmit(onDuplicate)}
        >
          Duplicate
        </Button>
      </Footer>
    </>
  );
}
